/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Request status vocabulary for the UI. MIRRORS backend/utils/requestStatus.js
 * — the two must be kept in step.
 *
 *   New       — submitted, nobody has picked it up.
 *   Pending   — an admin has taken it on.
 *   Completed — the work is done (requests are completed, not approved).
 *   Rejected  — the facility will not do it.
 *
 * Moves are always manual, so a request only leaves New because an admin said
 * so. normalizeRequestStatus() folds the pre-rename values ('Approved' and
 * 'Revision Required') into the new tabs so a request that has not been
 * migrated yet still renders in the right place.
 */

export const REQUEST_STATUSES = ['New', 'Pending', 'Completed', 'Rejected'];

const LEGACY_STATUS_MAP = {
  Approved: 'Completed',
  'Revision Required': 'Pending'
};

export const normalizeRequestStatus = (status) => {
  if (!status) return 'New';
  if (LEGACY_STATUS_MAP[status]) return LEGACY_STATUS_MAP[status];
  return REQUEST_STATUSES.includes(status) ? status : 'New';
};

/** MUI colour per status, used for chips. */
export const REQUEST_STATUS_COLOR = {
  New: 'info',
  Pending: 'warning',
  Completed: 'success',
  Rejected: 'error'
};

export const requestStatusColor = (status) =>
  REQUEST_STATUS_COLOR[normalizeRequestStatus(status)] || 'default';

/** CSS modifier class for the request-review status pill. */
export const REQUEST_STATUS_CLASS = {
  New: 'status-new',
  Pending: 'status-pending',
  Completed: 'status-completed',
  Rejected: 'status-rejected'
};

export const requestStatusClass = (status) =>
  REQUEST_STATUS_CLASS[normalizeRequestStatus(status)] || '';

/** Label to show for a stored status (legacy values show their new name). */
export const requestStatusLabel = (status) => normalizeRequestStatus(status);

export const isOpenRequestStatus = (status) =>
  ['New', 'Pending'].includes(normalizeRequestStatus(status));

export const REQUEST_STATUS_TABS = REQUEST_STATUSES.map(key => ({ key, label: key }));

const requestStatusUtils = {
  REQUEST_STATUSES,
  REQUEST_STATUS_TABS,
  normalizeRequestStatus,
  requestStatusColor,
  requestStatusClass,
  requestStatusLabel,
  isOpenRequestStatus
};

export default requestStatusUtils;
